import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faSync, 
  faCheck, 
  faTimes, 
  faSpinner, 
  faDownload, 
  faSchool,
  faSearch,
  faRedo,
  faInfoCircle
} from '@fortawesome/free-solid-svg-icons';
import { triggerAction, fetchDeviceVitals } from '../../services/fetchApis';
import UserService from '../../utils/userService';
import "./oneClick.css"

function TriggerOneClick() {
  const [schoolId, setSchoolId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [status, setStatus] = useState('');
  const [devices, setDevices] = useState([]);
  const [completedCount, setCompletedCount] = useState(0);
  const [isButtonDisabled, setIsButtonDisabled] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [filteredDevices, setFilteredDevices] = useState([]);
  
  useEffect(() => {
    // Check if this school was already processed today
    const checkSchoolProcessed = () => {
      const processedSchools = JSON.parse(localStorage.getItem('processedSchools') || '{}');
      const today = new Date().toISOString().split('T')[0];
      
      if (schoolId && processedSchools[schoolId] === today) {
        setIsButtonDisabled(true);
        setStatus(`School ID ${schoolId} was already processed today`);
      } else {
        setIsButtonDisabled(false);
        setStatus('');
      }
    };
    
    checkSchoolProcessed();
  }, [schoolId]);
  
  useEffect(() => {
    if (devices.length > 0) {
      setFilteredDevices(
        devices.filter(device => 
          device.teacherId?.toString().includes(searchQuery) ||
          device.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
          device.teacherMobile?.includes(searchQuery) ||
          device.deviceId?.includes(searchQuery)
        )
      );
    }
  }, [searchQuery, devices]);
  
  const fetchDevices = async () => {
    if (!schoolId) {
      setStatus('Please enter a School ID');
      return;
    }
    
    setIsLoading(true);
    setStatus('Fetching devices for school...');
    setDevices([]);
    setFilteredDevices([]);
    
    try {
      const data = await fetchDeviceVitals(schoolId);
      
      if (data.data && data.data.getDeviceVitals && data.data.getDeviceVitals.tabVitals) {
        const deviceData = data.data.getDeviceVitals.tabVitals.records.map(device => ({
          ...device,
          triggerStatus: 'pending',
          response: null
        }));
        
        setDevices(deviceData);
        setFilteredDevices(deviceData);
        setStatus(`Found ${deviceData.length} devices. Ready to trigger 1-click.`);
      } else {
        setStatus('No devices found or error in response');
      }
    } catch (error) {
      setStatus(`Error fetching devices: ${error.message}`);
    } finally {
      setIsLoading(false);
    }
  };
  
  const triggerOneClick = async () => {
    if (devices.length === 0) {
      setStatus('No devices to trigger');
      return;
    }
    
    setStatus('Triggering 1-click on all devices...');
    setCompletedCount(0);
    
    // Mark this school as processed today
    const processedSchools = JSON.parse(localStorage.getItem('processedSchools') || '{}');
    const today = new Date().toISOString().split('T')[0];
    processedSchools[schoolId] = today;
    localStorage.setItem('processedSchools', JSON.stringify(processedSchools));
    setIsButtonDisabled(true);
    
    const updatedDevices = [...devices];
    
    for (let i = 0; i < updatedDevices.length; i++) {
      const device = updatedDevices[i];
      setStatus(`Triggering device ${i + 1} of ${updatedDevices.length} (${device.name || 'Unknown'})`);
      
      updatedDevices[i] = { ...device, triggerStatus: 'in-progress' };
      setDevices([...updatedDevices]);
      setFilteredDevices(filteredDevices.map(d => 
        d.deviceId === device.deviceId ? { ...d, triggerStatus: 'in-progress' } : d
      ));
      
      try {
        if (device.fcmToken) {
          const responseData = await triggerAction(
            device.fcmToken,
            device.teacherMobile || '',
            'run_1_click',
            'TA',
            null
          );
          
          updatedDevices[i] = { 
            ...device, 
            triggerStatus: 'completed', 
            response: responseData 
          };
        } else {
          updatedDevices[i] = { 
            ...device, 
            triggerStatus: 'failed', 
            response: { error: 'No FCM token available' } 
          };
        }
      } catch (error) {
        updatedDevices[i] = { 
          ...device, 
          triggerStatus: 'failed', 
          response: { error: error.message } 
        };
      }
      
      setDevices([...updatedDevices]);
      setFilteredDevices(
        filteredDevices.map(d => 
          d.deviceId === updatedDevices[i].deviceId ? updatedDevices[i] : d
        )
      );
      setCompletedCount(i + 1);
    }
    
    setStatus(`Completed triggering 1-click for ${completedCount} devices. Check the status below.`);
    
    if (Notification.permission === 'granted') {
      new Notification('1-Click Trigger Complete', {
        body: `Completed triggering 1-click for ${completedCount} devices in School ID ${schoolId}.`
      });
    } else if (Notification.permission !== 'denied') {
      Notification.requestPermission().then(permission => {
        if (permission === 'granted') {
          new Notification('1-Click Trigger Complete', {
            body: `Completed triggering 1-click for ${completedCount} devices in School ID ${schoolId}.`
          });
        }
      });
    }
  };
  
  const retriggerSingleDevice = async (index, deviceId) => {
    // Find the device in the original array
    const deviceIndex = devices.findIndex(d => d.deviceId === deviceId);
    if (deviceIndex === -1) return;
    
    const device = devices[deviceIndex];
    const updatedDevices = [...devices];
    
    updatedDevices[deviceIndex] = { ...device, triggerStatus: 'in-progress' };
    setDevices(updatedDevices);
    setFilteredDevices(
      filteredDevices.map(d => 
        d.deviceId === deviceId ? { ...d, triggerStatus: 'in-progress' } : d
      )
    );
    
    try {
      if (device.fcmToken) {
        const responseData = await triggerAction(
          device.fcmToken,
          device.teacherMobile || '',
          'run_1_click',
          'TA',
          null
        );
        
        updatedDevices[deviceIndex] = { 
          ...device, 
          triggerStatus: 'completed', 
          response: responseData 
        };
        
        setStatus(`Re-triggered 1-click for device ${device.name || 'Unknown'}`);
      } else {
        updatedDevices[deviceIndex] = { 
          ...device, 
          triggerStatus: 'failed', 
          response: { error: 'No FCM token available' } 
        };
        setStatus(`Failed to re-trigger: No FCM token for device ${device.name || 'Unknown'}`);
      }
    } catch (error) {
      updatedDevices[deviceIndex] = { 
        ...device, 
        triggerStatus: 'failed', 
        response: { error: error.message } 
      };
      setStatus(`Error re-triggering device: ${error.message}`);
    }
    
    setDevices(updatedDevices);
    setFilteredDevices(
      filteredDevices.map(d => 
        d.deviceId === deviceId ? updatedDevices[deviceIndex] : d
      )
    );
  };
  
  const exportToCSV = () => {
    if (devices.length === 0) {
      setStatus('No data to export');
      return;
    }
    
    let csvContent = "data:text/csv;charset=utf-8,";
    const headers = [
      "Teacher ID", 
      "Name", 
      "Mobile", 
      "Device ID", 
      "Status", 
      "Trigger Status", 
      "Response"
    ];
    
    csvContent += headers.join(",") + "\n";
    
    devices.forEach(device => {
      const row = [
        device.teacherId || "",
        device.name || "",
        device.teacherMobile || "",
        device.deviceId || "",
        device.status || "",
        device.triggerStatus || "",
        device.response ? JSON.stringify(device.response).replace(/,/g, " ") : ""
      ];
      
      csvContent += row.join(",") + "\n";
    });
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `school_${schoolId}_1click_report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    
    link.click();
    document.body.removeChild(link);
    
    setStatus("CSV Export complete!");
  };
  
  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending':
        return <FontAwesomeIcon icon={faSync} className="icon pending" />;
      case 'in-progress':
        return <FontAwesomeIcon icon={faSpinner} className="icon in-progress spin" />;
      case 'completed':
        return <FontAwesomeIcon icon={faCheck} className="icon success" />;
      case 'failed':
        return <FontAwesomeIcon icon={faTimes} className="icon error" />;
      default:
        return null;
    }
  };
  
  const getProgressStats = () => {
    if (devices.length === 0) return { completed: 0, failed: 0, pending: 0, inProgress: 0 };
    
    return devices.reduce(
      (acc, device) => {
        switch (device.triggerStatus) {
          case 'completed':
            acc.completed++;
            break;
          case 'failed':
            acc.failed++;
            break;
          case 'in-progress':
            acc.inProgress++;
            break;
          case 'pending':
          default:
            acc.pending++;
        }
        return acc;
      },
      { completed: 0, failed: 0, pending: 0, inProgress: 0 }
    );
  };
  
  const stats = getProgressStats();
  const completedPercentage = devices.length ? Math.round((stats.completed / devices.length) * 100) : 0;
  
  return (
    <div className="oneclick-container">
      <header className="oneclick-header">
        <div className="oneclick-title">
          <FontAwesomeIcon icon={faSchool} className="school-icon" /> 
          <h1>School Device 1-Click Trigger</h1>
        </div>
        <div className="status-bar">
          {status && (
            <div className={`status-message ${isLoading ? 'loading' : ''}`}>
              {isLoading && <FontAwesomeIcon icon={faSpinner} className="spin status-icon" />}
              {status}
            </div>
          )}
        </div>
      </header>
      
      <div className="oneclick-content">
        <div className="control-panel">
          <div className="input-group">
            <label htmlFor="schoolId">School ID</label>
            <input
              type="text"
              id="schoolId"
              value={schoolId}
              onChange={(e) => setSchoolId(e.target.value)}
              placeholder="Enter School ID"
              className="school-input"
            />
          </div>
          
          <div className="button-group">
            <button
              className="fetch-button" 
              onClick={fetchDevices}
              disabled={isLoading}
            >
              <FontAwesomeIcon icon={faSearch} className="button-icon" />
              Fetch Devices
            </button>
            
            <button
              className={`trigger-button ${isButtonDisabled ? 'disabled' : ''}`}
              onClick={triggerOneClick}
              disabled={isLoading || devices.length === 0 || isButtonDisabled}
            >
              <FontAwesomeIcon icon={faSync} className="button-icon" />
              Trigger 1-Click
            </button>
            
            <button
              className="export-button"
              onClick={exportToCSV}
              disabled={devices.length === 0}
            >
              <FontAwesomeIcon icon={faDownload} className="button-icon" /> 
              Export CSV
            </button>
          </div>
        </div>
        
        {devices.length > 0 && (
          <div className="device-dashboard">
            <div className="stats-panel">
              <div className="stat-box">
                <span className="stat-value">{devices.length}</span>
                <span className="stat-label">Total Devices</span>
              </div>
              <div className="stat-box completed">
                <span className="stat-value">{stats.completed}</span>
                <span className="stat-label">Completed</span>
              </div>
              <div className="stat-box failed">
                <span className="stat-value">{stats.failed}</span>
                <span className="stat-label">Failed</span>
              </div>
              <div className="stat-box pending">
                <span className="stat-value">{stats.pending + stats.inProgress}</span>
                <span className="stat-label">Pending</span>
              </div>
            </div>
            
            {stats.completed > 0 && (
              <div className="progress-container">
                <div className="progress-bar">
                  <div 
                    className="progress-fill" 
                    style={{ width: `${completedPercentage}%` }}
                  >
                    {completedPercentage > 10 && `${completedPercentage}%`}
                  </div>
                </div>
                {completedPercentage <= 10 && <span className="progress-text">{completedPercentage}%</span>}
              </div>
            )}
            
            <div className="device-list-header">
              <h2>Devices ({filteredDevices.length})</h2>
              <div className="device-search">
                <input
                  type="text"
                  placeholder="Search devices..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="search-input"
                />
              </div>
            </div>
            
            <div className="device-list">
              <table>
                <thead>
                  <tr>
                    <th>Teacher ID</th>
                    <th>Name</th>
                    <th>Mobile</th>
                    <th>Device ID</th>
                    <th>Status</th>
                    <th>1-Click Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredDevices.map((device, index) => (
                    <tr 
                      key={device.deviceId || index} 
                      className={`device-row ${device.triggerStatus}`}
                    >
                      <td>{device.teacherId}</td>
                      <td>{device.name}</td>
                      <td>{device.teacherMobile}</td>
                      <td className="device-id">{device.deviceId}</td>
                      <td>{device.status}</td>
                      <td className="status-cell">
                        {getStatusIcon(device.triggerStatus)}
                        <span className="status-text">{device.triggerStatus}</span>
                      </td>
                      <td>
                        <button
                          className="retrigger-button"
                          onClick={() => retriggerSingleDevice(index, device.deviceId)}
                          disabled={device.triggerStatus === 'in-progress'}
                        >
                          <FontAwesomeIcon icon={faRedo} className="button-icon-sm" />
                          Re-trigger
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
        
        {!devices.length && !isLoading && (
          <div className="empty-state">
            <FontAwesomeIcon icon={faInfoCircle} className="empty-icon" />
            <p>Enter a School ID and click "Fetch Devices" to start.</p>
          </div>
        )}
      </div>
    </div>
  );
}

export default TriggerOneClick;